import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import TrashIcon from '@assets/icons/trash-small-thin.svg';
import {
  CompetitorData,
  QuoteLineItem,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-state-types';
import { CompetitorFieldsCell } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/competitor-fields-cell';
import { createColumnHelper, flexRender } from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import {
  CustomColumnDef,
  RenderHeaderRowCallback,
  RenderRowCallback,
} from '@experiences/components/table/types';
import { EditablePerUnitCell } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/editable-per-unit-cell';
import { EditablePriceCell } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/editable-price-cell';
import { EditableQuantityCell } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/editable-quantity-cell';
import { formatCurrency, MICRO_CONVERSION } from '@/utils/money';
import { IconButton } from '@usepepper/sprout-kit-ui';
import { LayoutChangeEvent, Platform, View, ViewStyle } from 'react-native';
import { Table } from '@experiences/components/table/table';
import { TableCell } from '@experiences/components/table/table-cell';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';
import { TableRow } from '@experiences/components/table/table-row';
import { Typography } from '@usepepper/ui/atoms/typography';

// Slightly higher row height on native is required to prevent two line cells from clipping
// due to differences in layout engine
const ROW_HEIGHT = Platform.OS === 'web' ? 57 : 60;

// Split layout — when a competitor is set, the table renders as two
// side-by-side sub-tables. 50/50 split per design.
const STANDARD_SPLIT_RATIO = 0.5;
const COMPETITOR_SPLIT_RATIO = 0.5;

enum ColumnId {
  CompetitorItem = 'competitorItem',
  CompetitorPackSize = 'competitorPackSize',
  CompetitorPrice = 'competitorPrice',
  Description = 'itemDescription',
  ItemCode = 'itemCode',
  Name = 'itemName',
  OurPrice = 'ourPrice',
  PackSize = 'itemPackSize',
  PerUnit = 'perUnit',
  Quantity = 'quantity',
  Remove = 'remove',
  Savings = 'savings',
  Total = 'total',
}

// Percent allocations sized so each column's share at the typical desktop
// content width (~936px) meets its `minSize`, avoiding clamp-driven overflow.
const COLUMN_PERCENT = {
  description: 11,
  itemCode: 9,
  name: 19,
  packSize: 11,
  perUnit: 11,
  price: 11,
  quantity: 8,
  remove: 5,
  total: 15,
};

// Competitor side claims 50% of the table width per the split layout;
// percents tuned so each column meets its `minSize` at that share.
const COMPETITOR_COLUMN_PERCENT = {
  itemName: 28,
  packSize: 22,
  price: 25,
  savings: 25,
};

const PRICE_COLUMN_MIN_WIDTH = 100;
const PRICE_COLUMN_MAX_WIDTH = 120;
const TOTAL_COLUMN_MIN_WIDTH = 140;
const QUANTITY_COLUMN_WIDTH = 72;
const PACK_SIZE_COLUMN_MIN_WIDTH = 100;
const PACK_SIZE_COLUMN_MAX_WIDTH = 120;

const columnHelper = createColumnHelper<QuoteLineItem>();

interface QuoteLineItemsTableProps {
  /** Heading shown above the standard table when a competitor is set. */
  accountName: string;
  bodyContainerStyle?: ViewStyle;
  /**
   * Heading shown above the competitor table. When non-null, the layout
   * splits 50/50 (standard | competitor). When null, the table renders as
   * a single full-width view.
   */
  competitorName: string | null;
  lineItems: QuoteLineItem[];
  onRemoveItem: (localId: string) => void;
  onUpdateCompetitor: (localId: string, competitor: CompetitorData) => void;
  onUpdatePrice: (localId: string, price: string) => void;
  onUpdateQuantity: (localId: string, quantity: string) => void;
}

/**
 * Line-items table for the Price Quotes editor. Renders each quote line
 * (catalog or custom) with inline-editable price + quantity cells, a
 * computed line total, and a remove action. Reads data from `useQuoteState`
 * via the parent screen; dispatches `UPDATE_PRICE`, `UPDATE_QUANTITY`,
 * `UPDATE_COMPETITOR`, and `REMOVE_ITEM` back through the passed callbacks.
 *
 * When `competitorName` is non-null, the layout splits 50/50:
 * - left: standard columns (item code / name / description / pack size /
 *   our price / per-unit / qty / total / remove)
 * - right: competitor columns (competitor item / pack size / price / savings)
 *
 * Pack size is read-only on the account side and editable on the
 * competitor side.
 *
 * The table manages its own virtualized scroll (TanStack Virtual) and sizes
 * columns to the available layout width.
 */
export const QuoteLineItemsTable: React.FC<QuoteLineItemsTableProps> = ({
  accountName,
  bodyContainerStyle,
  competitorName,
  lineItems,
  onRemoveItem,
  onUpdateCompetitor,
  onUpdatePrice,
  onUpdateQuantity,
}) => {
  const isCompetitive = competitorName !== null;
  const [styles, theme] = useStyles();
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);

  const handleLayout = (event: LayoutChangeEvent) => {
    setLayoutWidth(event.nativeEvent.layout.width);
  };

  const standardColumns = useMemo(
    () =>
      [
        columnHelper.accessor('itemCode', {
          cell: (info) => (
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={1}
              variant="paragraph-md"
            >
              {info.getValue()}
            </Typography>
          ),
          header: i18n.t('sales_portal.price_quotes.table.item_code'),
          id: ColumnId.ItemCode,
          meta: { initialPercentWidth: COLUMN_PERCENT.itemCode },
          minSize: 80,
        }),
        columnHelper.accessor('itemName', {
          cell: (info) => (
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={2}
              variant="paragraph-md-600"
            >
              {info.getValue()}
            </Typography>
          ),
          header: i18n.t('sales_portal.price_quotes.table.item_name'),
          id: ColumnId.Name,
          meta: { initialPercentWidth: COLUMN_PERCENT.name },
          minSize: 150,
        }),
        columnHelper.accessor('itemDescription', {
          cell: (info) => (
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={2}
              variant="paragraph-md"
            >
              {info.getValue()}
            </Typography>
          ),
          header: i18n.t('sales_portal.price_quotes.table.description'),
          id: ColumnId.Description,
          meta: { initialPercentWidth: COLUMN_PERCENT.description },
          minSize: 100,
        }),
        columnHelper.accessor('itemPackSize', {
          cell: (info) => (
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={2}
              variant="paragraph-md"
            >
              {info.getValue()}
            </Typography>
          ),
          header: i18n.t('sales_portal.price_quotes.table.pack_size'),
          id: ColumnId.PackSize,
          meta: { initialPercentWidth: COLUMN_PERCENT.packSize },
          minSize: PACK_SIZE_COLUMN_MIN_WIDTH,
        }),
        columnHelper.accessor('ourPrice', {
          cell: (info) => (
            <EditablePriceCell
              currencyCode={info.row.original.currencyCode}
              localId={info.row.original.localId}
              onUpdatePrice={onUpdatePrice}
              price={info.getValue()}
              rowIndex={info.row.index}
            />
          ),
          header: i18n.t('sales_portal.price_quotes.table.price'),
          id: ColumnId.OurPrice,
          maxSize: PRICE_COLUMN_MAX_WIDTH,
          meta: { initialPercentWidth: COLUMN_PERCENT.price },
          minSize: PRICE_COLUMN_MIN_WIDTH,
        }),
        columnHelper.display({
          cell: (info) => (
            <EditablePerUnitCell
              currencyCode={info.row.original.currencyCode}
              localId={info.row.original.localId}
              onUpdatePrice={onUpdatePrice}
              ourPrice={info.row.original.ourPrice}
              rowIndex={info.row.index}
              unitsPerPack={info.row.original.unitsPerPack}
            />
          ),
          header: i18n.t('sales_portal.price_quotes.table.per_unit'),
          id: ColumnId.PerUnit,
          maxSize: PRICE_COLUMN_MAX_WIDTH,
          meta: { initialPercentWidth: COLUMN_PERCENT.perUnit },
          minSize: PRICE_COLUMN_MIN_WIDTH,
        }),
        columnHelper.accessor('quantity', {
          cell: (info) => (
            <EditableQuantityCell
              localId={info.row.original.localId}
              onUpdateQuantity={onUpdateQuantity}
              quantity={info.getValue()}
              rowIndex={info.row.index}
            />
          ),
          header: i18n.t('sales_portal.price_quotes.table.quantity'),
          id: ColumnId.Quantity,
          maxSize: QUANTITY_COLUMN_WIDTH,
          meta: { initialPercentWidth: COLUMN_PERCENT.quantity },
          minSize: QUANTITY_COLUMN_WIDTH,
        }),
        columnHelper.display({
          cell: (info) => {
            const item = info.row.original;
            const price = parseFloat(item.ourPrice) || 0;
            const qty = parseFloat(item.quantity) || 0;
            const totalMicros = Math.round(price * qty * MICRO_CONVERSION);
            return (
              <Typography
                color={theme.sk.color.text.primary}
                numberOfLines={1}
                variant="paragraph-md"
              >
                {formatCurrency(totalMicros, item.currencyCode)}
              </Typography>
            );
          },
          header: i18n.t('sales_portal.price_quotes.table.total'),
          id: ColumnId.Total,
          meta: { initialPercentWidth: COLUMN_PERCENT.total },
          minSize: TOTAL_COLUMN_MIN_WIDTH,
        }),
        columnHelper.display({
          cell: (info) => (
            <IconButton
              aria-label={i18n.t('sales_portal.price_quotes.table.remove')}
              icon={
                <TrashIcon
                  height={16}
                  stroke={theme.sk.color.icon.tertiary}
                  width={16}
                />
              }
              onPress={() => onRemoveItem(info.row.original.localId)}
              shape="rounded"
              size="$sm"
              variant="tertiary"
            />
          ),
          header: '',
          id: ColumnId.Remove,
          maxSize: 50,
          meta: { initialPercentWidth: COLUMN_PERCENT.remove },
          minSize: 40,
        }),
      ] as CustomColumnDef<QuoteLineItem>[],
    [onRemoveItem, onUpdatePrice, onUpdateQuantity, theme],
  );

  const competitorColumns = useMemo(
    () =>
      [
        columnHelper.display({
          cell: (info) => (
            <CompetitorFieldsCell
              competitor={info.row.original.competitor}
              currencyCode={info.row.original.currencyCode}
              field="itemName"
              localId={info.row.original.localId}
              onUpdateCompetitor={onUpdateCompetitor}
              rowIndex={info.row.index}
            />
          ),
          header: i18n.t('sales_portal.price_quotes.table.item_name'),
          id: ColumnId.CompetitorItem,
          meta: { initialPercentWidth: COMPETITOR_COLUMN_PERCENT.itemName },
          minSize: 100,
        }),
        columnHelper.display({
          cell: (info) => (
            <CompetitorFieldsCell
              competitor={info.row.original.competitor}
              currencyCode={info.row.original.currencyCode}
              field="packSize"
              localId={info.row.original.localId}
              onUpdateCompetitor={onUpdateCompetitor}
              rowIndex={info.row.index}
            />
          ),
          header: i18n.t('sales_portal.price_quotes.table.pack_size'),
          id: ColumnId.CompetitorPackSize,
          maxSize: PACK_SIZE_COLUMN_MAX_WIDTH,
          meta: { initialPercentWidth: COMPETITOR_COLUMN_PERCENT.packSize },
          minSize: PACK_SIZE_COLUMN_MIN_WIDTH,
        }),
        columnHelper.display({
          cell: (info) => (
            <CompetitorFieldsCell
              competitor={info.row.original.competitor}
              currencyCode={info.row.original.currencyCode}
              field="price"
              localId={info.row.original.localId}
              onUpdateCompetitor={onUpdateCompetitor}
              rowIndex={info.row.index}
            />
          ),
          header: i18n.t('sales_portal.price_quotes.table.price'),
          id: ColumnId.CompetitorPrice,
          maxSize: PRICE_COLUMN_MAX_WIDTH,
          meta: { initialPercentWidth: COMPETITOR_COLUMN_PERCENT.price },
          minSize: PRICE_COLUMN_MIN_WIDTH,
        }),
        columnHelper.display({
          cell: (info) => {
            const item = info.row.original;
            if (!item.competitor?.price) {
              return null;
            }
            const ourPrice = parseFloat(item.ourPrice) || 0;
            const compPrice = parseFloat(item.competitor.price) || 0;
            if (compPrice <= 0) {
              return null;
            }
            const savingsInDollars = compPrice - ourPrice;
            const color =
              savingsInDollars > 0
                ? theme.sk.color.text.success
                : savingsInDollars < 0
                  ? theme.sk.color.text.error
                  : theme.sk.color.text.primary;
            return (
              <Typography
                color={color}
                numberOfLines={1}
                variant="paragraph-md"
              >
                {formatCurrency(
                  savingsInDollars * MICRO_CONVERSION,
                  item.currencyCode,
                )}
              </Typography>
            );
          },
          header: i18n.t('sales_portal.price_quotes.table.savings'),
          id: ColumnId.Savings,
          meta: { initialPercentWidth: COMPETITOR_COLUMN_PERCENT.savings },
          minSize: PRICE_COLUMN_MIN_WIDTH,
        }),
      ] as CustomColumnDef<QuoteLineItem>[],
    [onUpdateCompetitor, theme],
  );

  const renderHeaderRow = useCallback<RenderHeaderRowCallback<QuoteLineItem>>(
    ({ headerGroup }) => (
      <TableHeaderRow key={headerGroup.id}>
        {headerGroup.headers.map((header) => (
          <TableHeaderCell
            columnId={header.id}
            key={header.id}
            pinnedOffset={
              header.column.getIsPinned()
                ? header.column.getStart('left')
                : undefined
            }
            sortDirection={header.column.getIsSorted() || undefined}
          >
            {header.isPlaceholder ? null : (
              <Typography
                color={theme.sk.color.text.tertiary}
                numberOfLines={1}
                variant="paragraph-sm-600"
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </Typography>
            )}
          </TableHeaderCell>
        ))}
      </TableHeaderRow>
    ),
    [theme],
  );

  // Only pin row height in split mode — the left/right sub-tables must align row-for-row
  const renderRow = useCallback<RenderRowCallback<QuoteLineItem>>(
    ({ row }) => (
      <TableRow
        key={row.id}
        style={isCompetitive ? styles.fixedRow : undefined}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell cell={cell} key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ),
    [isCompetitive, styles.fixedRow],
  );

  const emptyComponent = (
    <View style={styles.emptyContainer}>
      <Typography color={theme.sk.color.text.tertiary} variant="paragraph-md">
        {i18n.t('sales_portal.price_quotes.table.empty')}
      </Typography>
    </View>
  );

  if (!layoutWidth) {
    return <View onLayout={handleLayout} style={styles.measureContainer} />;
  }

  if (isCompetitive) {
    return (
      <View onLayout={handleLayout} style={styles.tableContainer}>
        <View style={styles.splitContainer}>
          <View style={styles.standardSide}>
            <View style={styles.tableTitle}>
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-md-600"
              >
                {accountName}
              </Typography>
            </View>
            <View style={styles.standardTableArea}>
              <Table
                bodyContainerStyle={bodyContainerStyle}
                cellPadding={theme.sk.spacing.spaceMedium}
                columns={standardColumns}
                data={lineItems}
                // Pass empty view to satisfy table type but render as a special case sibling
                emptyComponent={<View />}
                estimatedRowHeight={ROW_HEIGHT}
                isActive
                layoutWidth={layoutWidth * STANDARD_SPLIT_RATIO}
                renderHeaderRow={renderHeaderRow}
                renderRow={renderRow}
                state={
                  lineItems.length > 0
                    ? {
                        columnPinning: {
                          left: [ColumnId.ItemCode, ColumnId.Name],
                        },
                      }
                    : undefined
                }
              />
              {/* Anchors the empty message to the viewport so it
               *  doesn't slide with the table's horizontal scroll. */}
              {lineItems.length === 0 ? (
                <View pointerEvents="none" style={styles.emptyOverlay}>
                  {emptyComponent}
                </View>
              ) : null}
            </View>
          </View>
          <View style={styles.tableDivider} />
          <View style={styles.competitorSide}>
            <View style={styles.tableTitle}>
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-md-600"
              >
                {competitorName}
              </Typography>
            </View>
            <Table
              bodyContainerStyle={bodyContainerStyle}
              cellPadding={theme.sk.spacing.spaceMedium}
              columns={competitorColumns}
              data={lineItems}
              // Empty state lives on the standard side; render an empty
              // view here so heights stay aligned but no message duplicates.
              emptyComponent={<View />}
              estimatedRowHeight={ROW_HEIGHT}
              isActive={false}
              layoutWidth={layoutWidth * COMPETITOR_SPLIT_RATIO}
              renderHeaderRow={renderHeaderRow}
              renderRow={renderRow}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View onLayout={handleLayout} style={styles.tableContainer}>
      <Table
        bodyContainerStyle={bodyContainerStyle}
        cellPadding={theme.sk.spacing.spaceMedium}
        columns={standardColumns}
        data={lineItems}
        emptyComponent={emptyComponent}
        estimatedRowHeight={ROW_HEIGHT}
        isActive
        layoutWidth={layoutWidth}
        renderHeaderRow={renderHeaderRow}
        renderRow={renderRow}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  competitorSide: {
    flex: 1,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.sk.spacing.space3xlarge,
  },
  emptyOverlay: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  fixedRow: {
    height: ROW_HEIGHT,
  },
  measureContainer: {
    flex: 1,
  },
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  standardSide: {
    flex: 1,
  },
  standardTableArea: {
    flex: 1,
  },
  tableContainer: {
    flex: 1,
  },
  tableDivider: {
    backgroundColor: theme.sk.color.border.primary,
    width: 1,
  },
  tableTitle: {
    borderBottomColor: theme.sk.color.border.primary,
    borderBottomWidth: 1,
    paddingBottom: theme.sk.spacing.spaceSmall,
    paddingLeft: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
}));
